/**
 * Take-it-with-you exports — PDF + QR + Markdown + WhatsApp share.
 *
 * All export paths take a typed PanelResult so the same code wraps the live
 * backend response (when the FastAPI server is wired) and the mock.
 */
import jsPDF from "jspdf";
import QRCode from "qrcode";

import type { PanelResult } from "../api/mock-result";
import { LANGUAGES } from "../data/samples";

// ---------------------------------------------------------------------------
// Markdown
// ---------------------------------------------------------------------------
export function toMarkdown(r: PanelResult, language: string): string {
  const lang = LANGUAGES[language] || language;
  const lines: string[] = [
    "# Panel — Your Contract Review",
    "",
    `**Urgency:** ${r.final_urgency_score}/10`,
    `**Language:** ${lang}`,
    "",
    "## Summary",
    "",
    r.recommendation.tldr,
    "",
    "## What to do",
    "",
    ...r.recommendation.action_items.map((a) => `- ${a}`),
    "",
    "## Save these contacts offline",
    "",
    ...r.recommendation.contacts.map(
      (c) => `- **${c.name}**${c.phone ? ` — ${c.phone}` : ""}${c.whatsapp ? ` — WhatsApp ${c.whatsapp}` : ""}`,
    ),
    "",
    "## Do NOT agree to",
    "",
    ...r.refusals.map((x) => `- **${x.refusal}** — ${x.reason}`),
    "",
    "## Ask the recruiter to change",
    "",
    ...r.pushbacks.map((p) => `- **Clause ${p.clause_number}:** ${p.ask}\n  - _Suggested:_ ${p.suggested}`),
    "",
    "## Where the panel disagrees",
    "",
    ...r.disagreement_reel.flatMap((d) => [
      `### #${d.rank} · ${d.topic} · severity ${d.severity}`,
      ...d.tensions.map((t) => `- **${t.agent}:** ${t.verdict}`),
      `*${d.why_it_matters}*`,
      "",
    ]),
    "",
    "---",
    "",
    "*Panel provides information only — not legal advice. For urgent help, contact your embassy.*",
  ];
  return lines.join("\n");
}

export function downloadMarkdown(r: PanelResult, language: string): void {
  triggerDownload(toMarkdown(r, language), "panel-review.md", "text/markdown");
}

// ---------------------------------------------------------------------------
// PDF
// ---------------------------------------------------------------------------
export function downloadPDF(r: PanelResult, language: string): void {
  const lang = LANGUAGES[language] || language;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const MARGIN = 56;
  const W = doc.internal.pageSize.getWidth();
  const RIGHT = W - MARGIN;
  let y = MARGIN;

  // Header strip
  doc.setFillColor(220, 38, 38);
  doc.rect(0, 0, W, 4, "F");

  // Title
  doc.setFont("times", "italic");
  doc.setFontSize(36);
  doc.setTextColor(15, 23, 42);
  doc.text("Panel", MARGIN, y + 20);

  // Subtitle
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(100, 116, 139);
  doc.text("A contract review · synthesised by six AI specialists", MARGIN, y + 40);
  y += 70;

  // Urgency card
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.roundedRect(MARGIN, y, RIGHT - MARGIN, 70, 8, 8);
  doc.setFont("times", "italic");
  doc.setFontSize(48);
  const urgencyColor = urgencyColorRGB(r.final_urgency_score);
  doc.setTextColor(...urgencyColor);
  doc.text(String(r.final_urgency_score), MARGIN + 20, y + 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(14);
  doc.setTextColor(100, 116, 139);
  doc.text("/ 10", MARGIN + 60, y + 50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text("URGENCY", MARGIN + 130, y + 30);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.text(urgencyVerdict(r.final_urgency_score), MARGIN + 130, y + 50);
  y += 90;

  // Meta line
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text(`Language: ${lang}    ·    Generated by Panel`, MARGIN, y);
  y += 24;

  // Summary
  y = sectionHeading(doc, "Summary", MARGIN, y);
  doc.setFont("times", "italic");
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  y = wrappedText(doc, r.recommendation.tldr, MARGIN, y, RIGHT - MARGIN, 16);
  y += 20;

  // What to do
  y = sectionHeading(doc, "What to do", MARGIN, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  for (const action of r.recommendation.action_items) {
    if (y > 760) { doc.addPage(); y = MARGIN; }
    doc.text("•", MARGIN, y);
    y = wrappedText(doc, action, MARGIN + 12, y, RIGHT - MARGIN - 12, 14);
    y += 4;
  }
  y += 14;

  // Contacts
  if (y > 700) { doc.addPage(); y = MARGIN; }
  y = sectionHeading(doc, "Save these contacts offline", MARGIN, y);
  for (const c of r.recommendation.contacts) {
    if (y > 760) { doc.addPage(); y = MARGIN; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(c.name, MARGIN, y);
    y += 14;
    doc.setFont("courier", "normal");
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    if (c.phone) { doc.text(c.phone, MARGIN + 8, y); y += 12; }
    if (c.whatsapp) { doc.text(`WhatsApp · ${c.whatsapp}`, MARGIN + 8, y); y += 12; }
    y += 6;
  }
  y += 10;

  // Do not agree to
  if (r.refusals.length) {
    if (y > 680) { doc.addPage(); y = MARGIN; }
    y = sectionHeading(doc, "Do NOT agree to", MARGIN, y, [220, 38, 38]);
    for (const x of r.refusals) {
      if (y > 760) { doc.addPage(); y = MARGIN; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      y = wrappedText(doc, "• " + x.refusal, MARGIN, y, RIGHT - MARGIN, 14);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      y = wrappedText(doc, x.reason, MARGIN + 12, y, RIGHT - MARGIN - 12, 13);
      y += 8;
    }
  }

  // Recruiter pushback
  if (r.pushbacks.length) {
    if (y > 680) { doc.addPage(); y = MARGIN; }
    y = sectionHeading(doc, "Ask the recruiter to change", MARGIN, y, [217, 119, 6]);
    for (const p of r.pushbacks) {
      if (y > 720) { doc.addPage(); y = MARGIN; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(`Clause ${p.clause_number}:`, MARGIN, y);
      doc.setFont("helvetica", "normal");
      doc.text(p.ask, MARGIN + 70, y);
      y += 14;
      doc.setFont("times", "italic");
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      y = wrappedText(doc, "Suggested: " + p.suggested, MARGIN + 8, y, RIGHT - MARGIN - 8, 13);
      y += 10;
    }
  }

  // Disagreement reel
  if (r.disagreement_reel.length) {
    if (y > 660) { doc.addPage(); y = MARGIN; }
    y = sectionHeading(doc, "Where the panel disagrees", MARGIN, y);
    for (const d of r.disagreement_reel) {
      if (y > 700) { doc.addPage(); y = MARGIN; }
      doc.setFont("times", "italic");
      doc.setFontSize(16);
      doc.setTextColor(15, 23, 42);
      doc.text(`#${d.rank} · ${d.topic} · sev ${d.severity}`, MARGIN, y);
      y += 18;
      for (const t of d.tensions) {
        if (y > 760) { doc.addPage(); y = MARGIN; }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(51, 65, 85);
        doc.text(t.agent + ":", MARGIN + 12, y);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(15, 23, 42);
        y = wrappedText(doc, t.verdict, MARGIN + 90, y, RIGHT - MARGIN - 90, 12);
        y += 2;
      }
      doc.setFont("times", "italic");
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      y = wrappedText(doc, d.why_it_matters, MARGIN + 12, y + 4, RIGHT - MARGIN - 12, 13);
      y += 12;
    }
  }

  // Footer disclaimer
  doc.addPage();
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text(
    "Panel provides information only — not legal advice.\nFor urgent help, contact your embassy.",
    MARGIN, MARGIN,
  );

  doc.save("panel-review.pdf");
}

// ---------------------------------------------------------------------------
// QR code — encodes a vCard for the embassy + summary
// ---------------------------------------------------------------------------
export async function generateQRDataURL(r: PanelResult): Promise<string> {
  const primary = r.recommendation.contacts[0];
  const payload = primary
    ? [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `FN:${primary.name}`,
        primary.phone ? `TEL:${primary.phone}` : "",
        primary.whatsapp ? `TEL;TYPE=WHATSAPP:${primary.whatsapp}` : "",
        "ORG:Panel — save before flying",
        `NOTE:Urgency ${r.final_urgency_score}/10. ${r.recommendation.tldr.slice(0, 120)}`,
        "END:VCARD",
      ].filter(Boolean).join("\n")
    : `PANEL · Urgency ${r.final_urgency_score}/10\n\n${r.recommendation.tldr}`;
  return QRCode.toDataURL(payload, {
    errorCorrectionLevel: "M",
    margin: 2,
    width: 320,
    color: { dark: "#0f172a", light: "#ffffff" },
  });
}

// ---------------------------------------------------------------------------
// WhatsApp share
// ---------------------------------------------------------------------------
export function whatsappShareURL(r: PanelResult): string {
  const lines: string[] = [
    `🛂 PANEL · Contract Review`,
    `Urgency: ${r.final_urgency_score}/10`,
    "",
    r.recommendation.tldr,
    "",
    "*What to do:*",
    ...r.recommendation.action_items.slice(0, 4).map((a) => `• ${a}`),
    "",
    "*Save offline:*",
    ...r.recommendation.contacts.map(
      (c) => `${c.name}${c.phone ? " · " + c.phone : ""}${c.whatsapp ? " · WA " + c.whatsapp : ""}`,
    ),
  ];
  return `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------
function triggerDownload(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function sectionHeading(
  doc: jsPDF,
  title: string,
  x: number,
  y: number,
  rgb: [number, number, number] = [100, 116, 139],
): number {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...rgb);
  doc.text(title.toUpperCase(), x, y);
  doc.setDrawColor(...rgb);
  doc.setLineWidth(0.3);
  doc.line(x, y + 4, x + 18, y + 4);
  return y + 22;
}

function wrappedText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number {
  const lines = doc.splitTextToSize(text, maxWidth);
  for (const line of lines) {
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

function urgencyColorRGB(score: number): [number, number, number] {
  if (score >= 8) return [220, 38, 38];
  if (score >= 5) return [234, 88, 12];
  if (score >= 3) return [245, 158, 11];
  return [21, 128, 61];
}

function urgencyVerdict(score: number): string {
  if (score >= 8) return "Critical — act before signing";
  if (score >= 5) return "Elevated — significant concerns";
  if (score >= 3) return "Moderate — proceed with eyes open";
  return "Low — contract largely compliant";
}
